/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { MutableRefObject, ReactNode } from 'react';

import {
  Carousel,
  CarouselNavigation,
  CarouselSlide,
  CarouselViewport,
  OverflowCarousel,
} from '@coursera-web/cds-labs';

import { Typography2, breakpoints } from '@coursera/cds-core';

export type StackabilityPanelValueProp = {
  icon?: ReactNode;
  heading: string;
  description?: string;
};

export type StackabilityPanelCard = {
  key: string;
  // Accessible name of the carousel slide.
  label?: string;
  node: ReactNode;
};

export type StackabilityPanelProps = {
  // Rendered as given, so the parent chooses the heading level, typography and spacing.
  heading: ReactNode;
  description?: ReactNode;
  valueProps?: StackabilityPanelValueProp[];
  carouselLabel: string;
  cards: StackabilityPanelCard[];
  // Replaces the desktop carousel with a horizontally scrolling row of fixed-width cards.
  scrollOnMobile?: boolean;
  // `compact` uses tighter spacing and a full-width panel on small screens.
  variant?: 'default' | 'compact';
  panelRef?: MutableRefObject<HTMLDivElement | null>;
};

const MOBILE_CARD_WIDTH = 252;

const styles = {
  panel: css`
    display: grid;
    grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
    gap: var(--cds-spacing-600);
    padding: var(--cds-spacing-600);
    border-radius: var(--cds-border-radius-300);
    background-color: var(--cds-color-neutral-background-primary-weak);

    ${breakpoints.down('md')} {
      grid-template-columns: minmax(0, 1fr);
    }
  `,
  panelDefault: css`
    ${breakpoints.down('md')} {
      gap: var(--cds-spacing-400);
    }

    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-300);
    }
  `,
  panelCompact: css`
    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-200);
      gap: var(--cds-spacing-200);
      border-radius: 0;
    }
  `,
  text: css`
    margin: 0;
  `,
  valueProps: css`
    display: grid;
    padding: 0;
    list-style: none;
  `,
  valuePropsDefault: css`
    gap: var(--cds-spacing-300);
    margin: var(--cds-spacing-400) 0 0;
  `,
  valuePropsCompact: css`
    gap: var(--cds-spacing-200);
    margin: var(--cds-spacing-300) 0 0;
  `,
  valueProp: css`
    display: grid;
    grid-template-columns: 24px minmax(0, 1fr);
    gap: var(--cds-spacing-150);
    align-items: start;
  `,
  icon: css`
    display: flex;
    align-items: flex-start;
  `,
  valuePropHeading: css`
    margin: 0 0 var(--cds-spacing-50);
  `,
  carousel: css`
    min-width: 0;
  `,
  navigation: css`
    display: flex;
    justify-content: flex-end;
  `,
  navigationDefault: css`
    margin-bottom: calc(var(--cds-spacing-200) + var(--cds-spacing-50) * 3);
  `,
  navigationCompact: css`
    margin-bottom: calc(var(--cds-spacing-50) * 3);
  `,
  slidesContainer: css`
    gap: calc(var(--cds-spacing-50) * 3);
  `,
  // The viewport padding and matching negative margin keep card shadows from being clipped.
  viewportBleed: css`
    margin: calc(var(--cds-spacing-50) * -3);
  `,
  viewport: css`
    box-sizing: border-box;
    padding: calc(var(--cds-spacing-50) * 3);
  `,
  slide: css`
    flex: 0 0 100%;
    min-width: 0;

    ${breakpoints.only('sm')} {
      flex-basis: calc((100% - var(--cds-spacing-50) * 3) / 2);
    }

    ${breakpoints.up('md')} {
      flex-basis: calc((100% - var(--cds-spacing-300)) / 3);
    }

    .cds-ProductCard-grid {
      min-width: 0;
    }
  `,
  // Cards keep a fixed width so the next card peeks in and the row scrolls.
  scrollRow: css`
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: ${MOBILE_CARD_WIDTH}px;
    gap: calc(var(--cds-spacing-50) * 3);
  `,
  // The row stretches every item to the tallest card; the card fills its item, so all cards match.
  scrollRowItem: css`
    height: 100%;
  `,
};

/** Panel with a heading, text and value props on the left and a row of cards on the right. */
const StackabilityPanel = ({
  heading,
  description,
  valueProps = [],
  carouselLabel,
  cards,
  scrollOnMobile = false,
  variant = 'default',
  panelRef,
}: StackabilityPanelProps) => {
  const isCompact = variant === 'compact';

  return (
    <div
      css={[styles.panel, isCompact ? styles.panelCompact : styles.panelDefault]}
      ref={panelRef}
      data-testid="stackability-panel"
    >
      <div>
        {heading}
        {description}
        {valueProps.length > 0 && (
          <ul css={[styles.valueProps, isCompact ? styles.valuePropsCompact : styles.valuePropsDefault]}>
            {valueProps.map(({ icon, heading: valuePropHeading, description: valuePropDescription }) => (
              <li key={valuePropHeading} css={styles.valueProp} data-testid="stackability-panel-value-prop">
                <span css={styles.icon}>{icon}</span>
                <div>
                  <Typography2 variant="subtitleMedium" component="h3" css={styles.valuePropHeading}>
                    {valuePropHeading}
                  </Typography2>
                  {valuePropDescription && (
                    <Typography2 variant="bodySecondary" color="supportText" component="p" css={styles.text}>
                      {valuePropDescription}
                    </Typography2>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div css={styles.carousel}>
        {scrollOnMobile ? (
          <OverflowCarousel css={styles.scrollRow}>
            {cards.map(({ key, node }) => (
              <div key={key} css={styles.scrollRowItem} data-testid="stackability-panel-scroll-card">
                {node}
              </div>
            ))}
          </OverflowCarousel>
        ) : (
          <Carousel options={{ slidesToScroll: 'auto' }} aria-label={carouselLabel}>
            <div css={[styles.navigation, isCompact ? styles.navigationCompact : styles.navigationDefault]}>
              <CarouselNavigation navigationVariant="default" />
            </div>
            <div css={styles.viewportBleed}>
              <CarouselViewport css={styles.viewport} containerCss={styles.slidesContainer}>
                {cards.map(({ key, label, node }) => (
                  <CarouselSlide key={key} slideCss={styles.slide} aria-label={label}>
                    {node}
                  </CarouselSlide>
                ))}
              </CarouselViewport>
            </div>
          </Carousel>
        )}
      </div>
    </div>
  );
};

export default StackabilityPanel;
